// Copyright (c) StrangeBee 2023

import { ItemProps } from '@Molecules/THForm/Item';
import { THForm, THFormProps } from '@Molecules/THForm/THForm';
import { Form, FormInstance } from 'antd';
import React, { ReactElement, memo, useEffect, useRef, useState } from 'react';
import { THEditableButtons } from '@Molecules/Inputs/THEditableButtons/THEditableButtons';

import './THEditable.scss';

interface THEditableProps<T> {
    children: ReactElement;
    initialValues?: Partial<T>;
    onFinish?: (values: T) => void;
    name: string;
    label?: string | React.ReactNode;
    horizontal?: boolean;
    submitOnChange?: boolean;
    formName?: string;
    formItemProps?: ItemProps<T>;
    formProps?: THFormProps<T>;
    className?: string;
    withDrawer?: boolean;
    onConfirmIsOpen?: (isOpen: boolean) => void;
    form?: FormInstance<T>;
}

const THEditableStock = <T,>({
    children,
    initialValues,
    onFinish,
    name,
    label,
    horizontal,
    formProps,
    formItemProps,
    submitOnChange,
    className,
    withDrawer = false,
    onConfirmIsOpen,
    form: formInstance,
}: THEditableProps<T>): ReactElement => {
    const [form] = Form.useForm(formInstance);
    const hiddenRef = useRef<any>(null);
    const [confirmationIsVisible, setConfirmationIsVisible] = useState<boolean>(false);

    const Buttons = !submitOnChange && !withDrawer && confirmationIsVisible && (
        <THEditableButtons
            onCancel={() => {
                form.resetFields();
                setConfirmationIsVisible(false);
            }}
        />
    );

    /**
     * Callback onConfirmIsOpen is called when confirmationIsVisible state change
     * this callback permits to execute an action when confirmation buttons are opening
     */
    useEffect(() => {
        onConfirmIsOpen?.(confirmationIsVisible);
    }, [confirmationIsVisible, onConfirmIsOpen]);

    return (
        <THForm
            editable
            form={form}
            className={className}
            onFinish={(values) => {
                onFinish?.(values);
                setConfirmationIsVisible(false);
                hiddenRef.current?.focus();
            }}
            initialValues={initialValues}
            onValuesChange={() => {
                if (withDrawer) {
                    // Prevent change selected value before validation
                    form.resetFields();
                } else {
                    submitOnChange && form.submit();
                }
                setConfirmationIsVisible(true);
            }}
            {...formProps}
        >
            <div className="th-editable__layout">
                <THForm.Item<T>
                    name={name}
                    label={label}
                    className="th-editable__form-item"
                    extraAction={!horizontal && Buttons}
                    {...formItemProps}
                >
                    {children}
                </THForm.Item>

                {horizontal && Buttons}
            </div>
        </THForm>
    );
};

export const THEditable = memo(THEditableStock) as typeof THEditableStock;
